import React, { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Box, Typography, Button } from "@mui/joy";
import BackBtn from "../../assets/svgs/Back-btn.svg";
import { useLocation, useNavigate } from "react-router-dom";
import PaymentSuccess from "components/modals/PaymentSuccess";
import PaymentFailure from "components/modals/PaymentFailure";
import ReactGA from "react-ga4";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { clearApplicant } from "../../redux/authSlice";

const CheckoutForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const secret = location?.state?.clientSecret;
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [hasPaymentIntent, setHasPaymentIntent] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const dispatch = useDispatch<AppDispatch>();

  const handleCloseSuccessModal = () => {
    navigate("/submission-complete");
    setHasPaymentIntent(false);
  };

  const paymentConfirmation = async () => {
    if (!stripe || !elements) return;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {},
      redirect: "if_required",
    });

    if (error) {
      console.error("Payment confirmation error:", error);
      setFailureMessage(error?.message || "An unknown error occurred.");
      setShowFailureModal(true);
    } else if (paymentIntent?.status === "succeeded") {
      console.log("Payment success:", paymentIntent);
      setIsLoading(false);
      setHasPaymentIntent(true);
      // dispatch(clearApplicant());
    } else {
      setFailureMessage(`Payment failed with status: ${paymentIntent?.status}`);
      setShowFailureModal(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    ReactGA.event({
      category: "Pay",
      action: "Pay Clicked",
      label: "Pay Clicked",
    });
    setIsLoading(true);
    paymentConfirmation();
  };

  const handleBackClick = () => {
    navigate("/thank-you");
  };

  const handleRetryPayment = () => {
    setShowFailureModal(false);
    setIsLoading(false);
    setFailureMessage("");
    navigate("/payment-method");
  };

  const handleCancelPayment = () => {
    setShowFailureModal(false);
    setIsLoading(false);
    setFailureMessage("");
    navigate("/thank-you");
  };

  return (
    <>
      <PaymentFailure
        open={showFailureModal}
        onRetry={handleRetryPayment}
        onCancel={handleCancelPayment}
        message={failureMessage}
      />
      <PaymentSuccess
        open={hasPaymentIntent}
        onClose={handleCloseSuccessModal}
      />

      <Box className="min-h-screen bg-[#f4f4f4] flex flex-col px-4 py-6 lg:px-10">
        <Box
          className="max-w-2xl mx-auto w-full"
          sx={{
            mb: { xs: 1, lg: 2 },
            display: "flex",
            justifyContent: "start",
            alignItems: "start",
          }}
        >
          <Box
            component="img"
            onClick={handleBackClick}
            src={BackBtn}
            alt="Back"
            sx={{
              width: { xs: 24, lg: 32 },
              height: { xs: 24, lg: 32 },
              cursor: "pointer",
            }}
          />
        </Box>

        <Box className="max-w-2xl mx-auto w-full">
          <Typography level="modalHeading" mb={2}>
            Payment method
          </Typography>

          <form onSubmit={handleSubmit} className="flex flex-col gap-8">
            <Box className="bg-white rounded-[16px] shadow-sm p-5 lg:p-6 mb-4">
              <PaymentElement />
            </Box>

            <Button type="submit" variant="solid" fullWidth loading={isLoading}>
              Continue
            </Button>
          </form>
        </Box>
      </Box>
    </>
  );
};

export default CheckoutForm;
